import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';
import {
  odooAuthenticate,
  odooDestroySession,
  fetchData,
  SESSION_EXPIRED_EVENT,
} from '@/api/apiClient';
import { cacheClear } from '@/lib/offlineCache';

/**
 * Single source of truth for Odoo authentication.
 *
 * We persist a small session object to localStorage so that a page reload
 * doesn't bounce the user back to the login screen — the underlying
 * Odoo session cookie is still valid on the server.
 *
 * We deliberately do NOT store the password in localStorage. The browser
 * holds the session cookie after login; that's enough for every API call.
 */

const SESSION_KEY = 'dashboard_session';
const CRED_KEY = 'dashboard_cred';

const OdooAuthContext = createContext(null);

export const OdooAuthProvider = ({ children }) => {
  const [odooSession, setOdooSession] = useState(null);
  const [isLoadingOdoo, setIsLoadingOdoo] = useState(true);

  /** Forget the local session; GatedApp then renders the login screen. */
  const clearLocalSession = () => {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(CRED_KEY);
    cacheClear();
    setOdooSession(null);
  };

  // The server no longer recognises our session cookie — drop the stale
  // local session instead of showing "non-JSON response" on every screen.
  // Several requests usually fail at once; only the first one acts, because
  // the session key is already gone for the rest.
  useEffect(() => {
    const onExpired = () => {
      if (!localStorage.getItem(SESSION_KEY)) return;
      clearLocalSession();
      toast.error('انتهت الجلسة، سجّل الدخول مرة أخرى');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        setOdooSession(session);
        // Refresh the role from the server in case the admin changed it
        // since the user last logged in. Best-effort — failures are ignored.
        refreshRole().catch(() => {});
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(CRED_KEY);
    }
    setIsLoadingOdoo(false);
  }, []);

  /**
   * Pull the user's SellGo-app role from the server (`/dashboard/api/user-role`)
   * and merge it into the persisted session.
   *
   * Returns the role string ('manager' | 'rep') or 'rep' as a safe default.
   */
  const refreshRole = async () => {
    try {
      // Prefer the namespaced path (introduced in addon v5.1.1) to avoid
      // any conflict with other addons. Fall back to the legacy path if
      // the server is still on an older module version.
      let data;
      try {
        data = await fetchData('sellgo-role');
      } catch {
        data = await fetchData('user-role');
      }
      const role = ['manager', 'supervisor', 'rep'].includes(data?.role) ? data.role : 'rep';
      setOdooSession((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          role,
          is_manager: !!data?.is_manager,
          is_supervisor: !!data?.is_supervisor,
          is_rep: !!data?.is_rep,
          is_system_admin: !!data?.is_system_admin,
          supervised_rep_uids: data?.supervised_rep_uids || [],
        };
        try {
          localStorage.setItem(SESSION_KEY, JSON.stringify(next));
        } catch {
          // localStorage may be unavailable; non-fatal
        }
        return next;
      });
      return role;
    } catch {
      return 'rep';
    }
  };

  /**
   * Perform login against Odoo and store the session.
   *
   * @param {{db: string, login: string, password: string}} creds
   * @returns {Promise<object>} the session object
   */
  const loginOdoo = async ({ db, login, password }) => {
    const result = await odooAuthenticate({ db, login, password });
    const session = {
      uid: result.uid,
      name: result.name || result.username || login,
      email: result.username || login,
      login: result.username || login,
      db: result.db || db,
      partner_id: result.partner_id,
      user_context: result.user_context,
      company_id: result.company_id,
      is_admin: !!(result.is_admin || result.is_system),
      // Role is filled in below from /dashboard/api/user-role.
      role: 'rep',
      is_manager: false,
      is_rep: true,
      is_system_admin: false,
    };

    // Persist the basic session first so the role-fetch is authenticated
    // by the same cookie that Odoo just set.
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    sessionStorage.setItem(CRED_KEY, password);
    setOdooSession(session);

    // Then enrich with the SellGo app role. Failures fall back to 'rep'.
    try {
      let data;
      try {
        data = await fetchData('sellgo-role');
      } catch {
        data = await fetchData('user-role');
      }
      const role = ['manager', 'supervisor', 'rep'].includes(data?.role) ? data.role : 'rep';
      const enriched = {
        ...session,
        role,
        is_manager: !!data?.is_manager,
        is_supervisor: !!data?.is_supervisor,
        is_rep: !!data?.is_rep,
        is_system_admin: !!data?.is_system_admin,
        supervised_rep_uids: data?.supervised_rep_uids || [],
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(enriched));
      setOdooSession(enriched);
      return enriched;
    } catch {
      return session;
    }
  };

  /** Destroy the server-side session and clear all local state. */
  const logoutOdoo = async () => {
    try {
      await odooDestroySession();
    } catch {
      // ignore — best effort
    }
    clearLocalSession();
  };

  const getCredential = () => sessionStorage.getItem(CRED_KEY) || '';

  return (
    <OdooAuthContext.Provider
      value={{ odooSession, isLoadingOdoo, loginOdoo, logoutOdoo, getCredential, refreshRole }}
    >
      {children}
    </OdooAuthContext.Provider>
  );
};

export const useOdooAuth = () => {
  const ctx = useContext(OdooAuthContext);
  if (!ctx) throw new Error('useOdooAuth must be used within OdooAuthProvider');
  return ctx;
};
